import { defineComponent as _defineComponent } from 'vue'
import { isEqual } from 'apollo-utilities';
import { computed, defineAsyncComponent, ref, watch } from 'vue';

import ContentHeader from '@/components/content-header/ContentHeader.vue';
import { TitleList, FilterBar } from '@/lazy-load-components';

import { useRoute } from 'vue-router/composables';
import { useI18n } from '@/helpers/composables/useI18n';
import { useQuery } from '@/helpers/composables/useApollo';
import { useFilterStore, useLanguageStore, useMetaStore, useUserStore } from '@/helpers/composables/useStores';

import {
	GetUpcomingTitlesDocument,
	GetUpcomingTitlesQuery,
	GetUpcomingTitlesQueryVariables,
} from '@/pages/graphql/queries/GetUpcomingTitles.query';

import { MonetizationType, NewPageType, Platform, TitleFilter } from '@/@types/graphql-types';
import { CollectionType } from '@/enums/collection-type';
import type { Content } from '@/interfaces/components/content';
import { TitleListLayout } from '@/enums/title-list-layout';
import { toGraphqlFilters } from '@/helpers/graphql-model-helper';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { TitleEventPayloadGraphql, trackTitleEventGraphql } from '@/helpers/title-helper';
import { toastError } from '@/helpers/toast/toast-helper';


export default /*#__PURE__*/_defineComponent({
  __name: 'Upcoming',
  setup(__props) {

const TitleListOptionsSeo = defineAsyncComponent(() => import('@/components/filter-bar/seo/TitleListOptionsSeo.vue'));
const FilterBarSeo = defineAsyncComponent(() => import('@/components/filter-bar/seo/FilterBarSeo.vue'));
const NewPageTypeSwitcher = defineAsyncComponent(() => import('@/components/nav/switcher/NewPageTypeSwitcher.vue'));
const NewPageTypeSwitcherSeo = defineAsyncComponent(
	() => import('@/components/nav/switcher/seo/NewPageTypeSwitcherSeo.vue')
);

const showFooter = ref(false);
const isFirstPageLoaded = ref(false);
const content = ref<Content | null>(null);

const { tc } = useI18n();
const route = useRoute();
const { language, country } = useLanguageStore();
const { filters, selectedProviders } = useFilterStore();
const { isHideSeenDislikedTitlesEnabled, isLoggedIn } = useUserStore();
const { setVisiblePosters } = useMetaStore();

const filterUpcoming = computed(() => filters.value(CollectionType.UPCOMING));

const graphqlFilters = computed<TitleFilter>(() => {
	const providers = selectedProviders.value(!isLoggedIn.value);
	return toGraphqlFilters(filterUpcoming.value, providers, isHideSeenDislikedTitlesEnabled.value);
});

const queryVariables = computed<GetUpcomingTitlesQueryVariables>(() => ({
	after: '',
	first: 32,
	pageType: NewPageType.Upcoming,
	filter: {
		packages: graphqlFilters.value.packages,
		monetizationTypes: graphqlFilters.value.monetizationTypes as MonetizationType[],
	},
	newTitlesFilter2: graphqlFilters.value,
	language: language.value,
	country: country.value,
	platform: Platform.Web,
}));

const { result, onResult, onError, fetchMore, loading } = useQuery<
	GetUpcomingTitlesQuery,
	GetUpcomingTitlesQueryVariables
>(GetUpcomingTitlesDocument, queryVariables, { fetchPolicy: 'cache-and-network' });

onResult(() => {
	isFirstPageLoaded.value = true;
	setVisiblePosters((result.value?.newTitles?.edges || []).length);
});

onError(() => {
	if (process.client) {
		toastError({ message: tc('WEBAPP_ALERT_ERROR_TITLE'), duration: 2000 });
	}
});

const upcomingPageData = computed(() => result.value?.newTitles);

const upcomingTitles = computed(() => upcomingPageData.value?.edges || []);

const titleAmount = computed(() => upcomingPageData.value?.totalCount || 0);

const isFirstPage = computed(() => !upcomingPageData.value?.pageInfo?.hasPreviousPage);

const isAtEndOfList = computed(() => !upcomingPageData.value?.pageInfo?.hasNextPage);

const showSpinner = computed(() => !isFirstPageLoaded.value && loading.value);

const newPageType = computed(() => route?.meta?.pageType ?? NewPageType.Upcoming);

async function onLoadNext({ event }: { event: any }) {
	if (!event) return;

	try {
		await fetchMore({
			variables: {
				after: upcomingPageData.value?.pageInfo.endCursor,
			},
			updateQuery(previousResult, { fetchMoreResult }) {
				if (!fetchMoreResult) return previousResult;

				return {
					newTitles: {
						__typename: previousResult.newTitles.__typename,
						totalCount: fetchMoreResult?.newTitles.totalCount,
						edges: [...(previousResult.newTitles.edges || []), ...(fetchMoreResult.newTitles.edges || [])],
						pageInfo: fetchMoreResult.newTitles.pageInfo,
					},
				};
			},
		});
	} catch (error: any) {
		captureMessageForSentry(
			'[GraphQL Upcoming Page Load More Titles Issue]:',
			{ error, where: 'Component: Upcoming, method: onLoadNext' },
			'error'
		);
	} finally {
		if (event.target) {
			event.target.complete(upcomingTitles.value.length);
		}
	}
}

watch(filterUpcoming, (newValue: any, oldValue: any) => {
	if (!isEqual(newValue, oldValue)) {
		isFirstPageLoaded.value = false;
		scrollToTop();
	}
});

function scrollToTop() {
	content.value?.scrollToTop(0, 'auto');
}

function trackClick(payload: TitleEventPayloadGraphql) {
	trackTitleEventGraphql('title_clicked', payload, 'upcoming-timeline');
}

return { __sfc: true,TitleListOptionsSeo, FilterBarSeo, NewPageTypeSwitcher, NewPageTypeSwitcherSeo, showFooter, isFirstPageLoaded, content, tc, route, language, country, filters, selectedProviders, isHideSeenDislikedTitlesEnabled, isLoggedIn, setVisiblePosters, filterUpcoming, graphqlFilters, queryVariables, result, onResult, onError, fetchMore, loading, upcomingPageData, upcomingTitles, titleAmount, isFirstPage, isAtEndOfList, showSpinner, newPageType, onLoadNext, scrollToTop, trackClick, ContentHeader, TitleList, FilterBar, CollectionType, TitleListLayout }
}

})